import { Button } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { useMemo } from "react";
import "./filters.scss"
import FilterPill from "components/common/FilterPill";
import FilterPillPopup from "components/common/FilterPillPopup";

interface AppliedFiltersProps {
    handleClearFilters: () => void;
    handleRemoveFilter?: (key: string) => void;
    filters: any;
    onFilterUpdate?: (key: string, value: any) => void;
    numbersOptions?: string[];
    queuesOptions?: string[];
    agentsOptions?: { label: string; value: string }[];
}

const DURATION_OPTIONS = [
    { value: 'lt1', label: 'Less than 1 minute' },
    { value: '1to5', label: '1 to 5 minutes' },
    { value: '5to15', label: '5 to 15 minutes' },
    { value: 'gt15', label: 'More than 15 minutes' },
];

export default function AppliedFilters({ 
    handleClearFilters, 
    handleRemoveFilter, 
    filters,
    onFilterUpdate,
    numbersOptions = [],
    queuesOptions = [],
    agentsOptions = []
}: AppliedFiltersProps) {
    const queues = useSelector((state: RootState) => state.apps.data.queue || []);
    const agents = useSelector((state: RootState) => state.agents.items);


    // Create queue options
    const queueOptions = useMemo(() => {
        return queuesOptions.map((queueName: string) => ({
            label: queueName,
            value: queueName
        }));
    }, [queuesOptions]);

    // Create number options
    const numberOptions = useMemo(() => {
        return numbersOptions.map((number: string) => ({
            label: number,
            value: number
        }));
    }, [numbersOptions]);

    // Create agent options from agents if not provided
    const agentOptions = useMemo(() => {
        if (agentsOptions.length > 0) return agentsOptions;
        return agents.map((agent: any) => ({
            label: agent.name || agent.id,
            value: agent.id
        }));
    }, [agentsOptions, agents]);

    const handleDeselectItem = (filterKey: string, value: string) => {
        if (!onFilterUpdate) return;
        
        const currentValues = filters[filterKey] || [];
        const updatedValues = currentValues.filter((v: string) => v !== value);
        
        if (updatedValues.length === 0) {
            onFilterUpdate(filterKey, null);
        } else {
            onFilterUpdate(filterKey, updatedValues);
        }
    };

    const handleClearFilter = (filterKey: string) => {
        if (onFilterUpdate) {
            onFilterUpdate(filterKey, null);
        }
    };

    const hasAny = Boolean(
        (filters.selectedAgent && filters.selectedAgent.length) ||
        (filters.selectedQueue && filters.selectedQueue.length) ||
        (filters.selectedNumber && filters.selectedNumber.length) ||
        filters.selectedDuration
    )

    if (!hasAny) return null

    const pills: { 
        key: string; 
        label: string; 
        value?: string;
        selectedValues?: string[];
        options?: { label: string; value: string }[];
    }[] = []

    if (filters.selectedAgent && filters.selectedAgent.length) {
        const agentNames = filters.selectedAgent
            .map((id: string) => agentOptions.find(opt => opt.value === id)?.label || id)
            .filter(Boolean);
        
        const agentValue = agentNames.length === 1 
            ? agentNames[0] 
            : `${agentNames.length} selected`;
        
        pills.push({ 
            key: 'selectedAgent', 
            label: 'Agents',
            value: agentValue,
            selectedValues: filters.selectedAgent,
            options: agentOptions
        });
    }

    if (filters.selectedQueue && filters.selectedQueue.length) {
        const queueValue = filters.selectedQueue.length === 1 
            ? filters.selectedQueue[0] 
            : `${filters.selectedQueue.length} selected`;
        
        pills.push({ 
            key: 'selectedQueue', 
            label: 'Queue',
            value: queueValue,
            selectedValues: filters.selectedQueue,
            options: queueOptions
        });
    }

    if (filters.selectedNumber && filters.selectedNumber.length) {
        const numberValue = filters.selectedNumber.length === 1 
            ? filters.selectedNumber[0] 
            : `${filters.selectedNumber.length} selected`;
        
        pills.push({ 
            key: 'selectedNumber', 
            label: 'Number',
            value: numberValue,
            selectedValues: filters.selectedNumber,
            options: numberOptions
        });
    }

    if (filters.selectedDuration) {
        const durationLabel = DURATION_OPTIONS.find(opt => opt.value === filters.selectedDuration)?.label || filters.selectedDuration;
        pills.push({ 
            key: 'selectedDuration', 
            label: 'Duration',
            value: durationLabel
        });
    }

    return (
        <div className="applied-filters-wrapper-scroll">
            <div className="applied-filters-wrapper">
                <p className="filter-label">Search Filters:</p>
                {pills.map((pill) => {
                    const pillContent = (
                        <FilterPill 
                            key={pill.key} 
                            name={pill.label} 
                            onRemove={() => handleRemoveFilter?.(pill.key)} 
                            value={pill.value}
                        />
                    );

                    if (pill.selectedValues && pill.selectedValues.length > 0 && pill.options && onFilterUpdate) {
                        return (
                            <FilterPillPopup
                                key={pill.key}
                                filterKey={pill.key}
                                filterLabel={pill.label}
                                selectedValues={pill.selectedValues}
                                options={pill.options}
                                onDeselect={(value) => handleDeselectItem(pill.key, value)}
                                onClearAll={() => handleClearFilter(pill.key)}
                            >
                                <div style={{ display: 'inline-block' }}>
                                    {pillContent}
                                </div>
                            </FilterPillPopup>
                        );
                    }

                    return pillContent;
                })}
                <Button className="clear-filter-button" onClick={handleClearFilters}>Clear All</Button>
            </div>
        </div>
    )
}